import React, { useEffect, useMemo, useState } from "react";
import { get } from "../../services/api";
import { MaterialReactTable } from "material-react-table";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import CommunicationModal from "./CommunicationModal";
import StudentProfile from "./student_profile/StudentProfile";
import CloseIcon from "@mui/icons-material/Close";
import StudentProfileModal from "./StudentProfileModal";
import FullScreenLoader from "../../components/FullScreenLoader";

function StudentList() {
  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");
  const roleId = localStorage.getItem("roleId");
  const userId = localStorage.getItem("userId");

  const navigate = useNavigate();

  const [columnLabels, setColumnLabels] = useState({
    id: "ID",
    firstName: "First Name",
    lastName: "Last Name",
    code: "Code",
    rollNo: "Roll No",
    dateOfBirth: "Date of Birth",
    centerName: "Center",
    className: "Class",
    sectionName: "Section",
    email: "Email",
  });
  const [studentsData, setStudentsData] = useState([]);
  const [columnOrder, setColumnOrder] = useState([
    "id",
    "firstName",
    "lastName",
    "code",
    "rollNo",
    "dateOfBirth",
    "centerName",
    "className",
    "sectionName",
    "email",
    "actions",
  ]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState([]);
  const [columnSizing, setColumnSizing] = useState({});
  const [filterModes, setFilterModes] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const fetchStudents = async () => {
    try {
      var savedUserId = roleId == 10 ? userId : 0; // Use === for comparison
      const response = await get(
        `Student/GetAllStudentByCenterIdAsync?centerId=${centerId}&userId=${savedUserId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        setStudentsData(response?.data);
        setColumnLabels(
          Object.fromEntries(
            response?.columns.map((col) => [col.key, col.label])
          )
        );
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    await fetchStudents();
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePrintStudentCardClick = async (firstRegistrationId) => {
    if (!firstRegistrationId) {
      alert("Error");
      return;
    }

    const API_BASE_URL = process.env.REACT_APP_API_URL;

    try {
      const response = await fetch(
        `${API_BASE_URL}Student/GenerateStudentCard?FirstRegistrationId=${firstRegistrationId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url);
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error opening the invoice:", error);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this student?"
    );
    if (!confirmed) return;
    try {
      const response = await get(
        `Student/DeleteStudentByIdAsync?userId=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success(response.message || "Student deleted successfully!");
        fetchData();
      } else {
        toast.error(response.message || "An error occurred while deleting.");
      }
    } catch (error) {
      console.error("Error deleting student:", error);
      toast.error("An error occurred while deleting the student.");
    }
  };

  const handleRowClick = (row) => {
    navigate(`/student-form?studentId=${row.original.id}`);
  };

  const columns = useMemo(
    () => [
      ...columnOrder
        .filter((key) => key !== "mrt-row-spacer" && key !== "actions")
        .map((key) => ({
          accessorKey: key,
          id: key,
          header: columnLabels[key] || key,
          filterVariant: key.includes("is") ? "checkbox" : "text",
          enableColumnFilter: true,
          enableColumnFilterModes: true,
          filterModeOptions: [
            "contains",
            "startsWith",
            "endsWith",
            "equals",
            "notEquals",
            "greaterThan",
            "lessThan",
            "greaterThanOrEqualTo",
            "lessThanOrEqualTo",
            "between",
            "empty",
            "notEmpty",
          ],
          filterFn:
            filterModes[key] ||
            (key.includes("Id") || key.includes("No") || key.startsWith("is")
              ? "equals"
              : key.includes("Date")
              ? "between"
              : "contains"),
          enableSorting: true,
          // size: columnSizing[key] || 150,
          minSize: 100,
          maxSize: 1000,
        })),
      {
        accessorKey: "actions",
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        enablePinning: true,
        pinned: "right",
        size: 120,
        Cell: ({ row }) => (
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="btn btn-with-icon-and-tooltip"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Student Profile"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStudent(row.original);
                setIsProfileModalOpen(true);
              }}
            >
              <i className="text-primary fs-8 fas fa-user-gear"></i>
            </button>
            {/* <button
              type="button"
              className="btn btn-with-icon-and-tooltip"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Print Student Card"
              onClick={(e) => {
                e.stopPropagation();
                handlePrintStudentCardClick(row.original.id);
              }}
            >
              <i className="text-primary fs-8 fas fa-print"></i>
            </button>
            <button
              type="button"
              className="btn btn-with-icon-and-tooltip"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original.id);
              }}
            >
              <i className="text-primary fs-8 fas fa-trash"></i>
            </button> */}
            {/* <button
              type="button"
              className="btn btn-with-icon-and-tooltip"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Communication"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStudent(row.original);
                setIsModalOpen(true);
              }}
            >
              <i className="text-primary fs-8 fas fa-comments"></i>
            </button> */}
          </div>
        ),
      },
    ],
    [columnOrder, columnLabels, filterModes, columnSizing]
  );

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(studentsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "students_data.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF("landscape");
    const pageWidth = doc.internal.pageSize.getWidth();
    // const logoWidth = 30;
    // const logoHeight = 8;
    // const logoX = (pageWidth - logoWidth) / 2;
    // const logoY = 10;
    // doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Students Data";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = 20;
    doc.text(title, titleX, titleY);

    const columns = columnOrder
      .filter((key) => key !== "mrt-row-spacer" && key !== "actions")
      .map((key) => ({
        header: columnLabels[key] || key,
        dataKey: key,
      }));

    const headers = [columns.map((col) => col.header)];

    const bodyData = studentsData.map((row) =>
      columns.map((col) => row[col.dataKey] ?? "")
    );

    doc.autoTable({
      head: headers,
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
        halign: "center",
      },
      styles: {
        // fontSize: 8,
        // cellPadding: 4,
        overflow: "linebreak",
        // minCellHeight: 20,
      },
    });

    doc.save("students_data.pdf");
  };

  return (
    <>
      <div className="page-header d-flex mb-3">
        <h2 className="page-heading fs-7">Students</h2>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="custom-mrt-table-wrapper">
            <MaterialReactTable
              columns={columns}
              data={studentsData}
              enableColumnFilters
              enableGlobalFilter
              enableColumnDragging
              enableColumnResizing
              enableHiding
              enableColumnOrdering
              enableSorting
              enableStickyHeader
              enableColumnFilterModes
              defaultColumn={{
                minSize: 150,
                maxSize: 1000,
                // size: 150,
              }}
              initialState={{
                showColumnFilters: true,
                columnOrder,
                columnVisibility,
                sorting,
                columnSizing,
                columnFilters,
                density: "compact",
                columnPinning: { right: ["actions"] },
              }}
              state={{
                columnOrder,
                columnVisibility,
                sorting,
                columnSizing,
                columnFilters,
              }}
              onColumnOrderChange={(newOrder) =>
                setColumnOrder(
                  newOrder.filter((key) => key !== "mrt-row-spacer")
                )
              }
              onColumnVisibilityChange={setColumnVisibility}
              onSortingChange={(updater) => {
                const newSorting =
                  typeof updater === "function" ? updater(sorting) : updater;
                setSorting(newSorting.slice(0, 1));
              }}
              onColumnSizingChange={(updater) => {
                const newSizing =
                  typeof updater === "function"
                    ? updater(columnSizing)
                    : updater;
                setColumnSizing(
                  Object.fromEntries(
                    Object.entries(newSizing).filter(
                      ([key]) => key !== "mrt-row-spacer"
                    )
                  )
                );
              }}
              onColumnFiltersChange={(updater) => {
                const newFilters =
                  typeof updater === "function"
                    ? updater(columnFilters)
                    : updater;
                setColumnFilters(
                  newFilters.filter((filter) => filter.id !== "mrt-row-spacer")
                );
                const newFilterModes = { ...filterModes };
                newFilters.forEach((filter) => {
                  if (filter.filterFn && filter.id !== "mrt-row-spacer") {
                    newFilterModes[filter.id] = filter.filterFn;
                  }
                });
                setFilterModes(newFilterModes);
              }}
              // muiTableBodyRowProps={({ row }) => ({
              //   onClick: () => handleRowClick(row),
              //   sx: { cursor: "pointer" },
              // })}
              muiTableProps={{
                className: "custom-mrt-table",
              }}
              muiTableHeadCellProps={{
                className: "custom-mrt-table-head-cell",
              }}
              muiTableBodyCellProps={{
                className: "custom-mrt-table-body-cell",
              }}
              renderTopToolbarCustomActions={({ table }) => (
                <div className="d-flex gap-1 align-items-center">
                  <button
                    type="button"
                    className="btn btn-with-icon-and-tooltip"
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="Export to Excel"
                    onClick={exportToExcel}
                  >
                    <i className="text-success fs-7 fas fa-file-excel"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-with-icon-and-tooltip"
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="Export to PDF"
                    onClick={exportToPDF}
                  >
                    <i className="text-danger fs-7 fas fa-file-pdf"></i>
                  </button>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      <CommunicationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        student={selectedStudent}
      />

      {isProfileModalOpen && (
        <StudentProfile
          student={selectedStudent}
          onClose={() => setIsProfileModalOpen(false)}
        />
      )}

      {loading && <FullScreenLoader />}
    </>
  );
}

export default StudentList;
